<template>
  <dialog
    ref="modalRef"
    class="dvp-dialog"
  >
    <div class="dvp-modal">
      <slot v-if="isOpen" />
    </div>
  </dialog>
</template>

<script setup>
  import { computed, onMounted, provide, ref, useSlots, useTemplateRef, watch } from 'vue'

  import { events$ } from '@shared/services'

  import { EVENT_MODAL } from '@shared/config/events'

  // HOOKS
  const slots = useSlots()

  // INIT
  const emit = defineEmits(['closed', 'opened'])
  const props = defineProps({
    id: {
      type: String,
      required: true
    }
  })

  // DATA
  const isOpen = ref(false)
  const modalRef = useTemplateRef('modalRef')

  // COMPUTED
  const childProps = computed(() => {
    return slots.default()[0]?.props || {}
  })

  // METHODS
  function close() {
    const event = new CustomEvent('cancel')
    modalRef.value.dispatchEvent(event)
  }

  function open() {
    events$.emit(EVENT_MODAL.OPEN, { modalName: props.id })

    modalRef.value.showModal()
    isOpen.value = true
  }

  function onClose() {
    events$.emit(EVENT_MODAL.CLOSE, {
      modalName: props.id,
      step: childProps.value.id
    })

    isOpen.value = false
  }

  function requestClose(event) {
    event.preventDefault()

    events$.emit(EVENT_MODAL.BEFORE_CLOSING, {
      modalName: props.id,
      step: childProps.value.id,
    })

    modalRef.value.close()
  }

  // LIFECYCLE HOOKS
  onMounted(() => {
    // @note: triggers request to close modal -> do async stuff
    modalRef.value.addEventListener('cancel', requestClose)

    // @note: triggers fn to close modal -> no time to do shit
    modalRef.value.addEventListener('close', onClose)
  })

  // WATCHER
  watch(isOpen, (modalState) => {
    if (modalState) {
      emit('opened')

    } else {
      emit('closed')
    }
  })

  // EPILOGUE
  provide('modalOptions', {
    isOpen,
    modalRef,
    props,
  })

  defineExpose({
    isOpen,
    modalRef,
    open,
    close,
  })
</script>

<style scoped>
  .dvp-dialog {
    position: fixed;
    max-height: 100%;
    max-width: 100%;
    height: 100%;
    width: 100%;
    inset: 0;
    padding: 0;
    margin: 0;
    border: none;
    background: transparent;
    opacity: 0;
    transition: opacity 0.2s, translate 0.2s, display 0.2s allow-discrete, overlay 0.2s allow-discrete;

    &[open] {
      display: flex;
      opacity: 1;
      translate: 0 0;

      &::backdrop {
        background: var(--scrim);
        backdrop-filter: blur(2px);
      }
    }

    &::backdrop {
      background: var(--scrim);
      backdrop-filter: blur(0px);
      transition:
        background 0.2s,
        backdrop-filter 0.2s,
        display 0.2s allow-discrete,
        overlay 0.2s allow-discrete;
    }

    .dvp-modal {
      background-color: var(--surface);
      box-shadow: var(--elevation-level-3);
      width: 100%;
    }
  }

  @starting-style {
    .dvp-dialog[open] {
      opacity: 0;
      translate: 0 8px;
    }

    .dvp-dialog[open]::backdrop {
      background: var(--scrim);
      backdrop-filter: blur(0px);
    }
  }
</style>

<style scoped>
  @media (--v-small) {
    .dvp-dialog .dvp-modal {
      border-top-left-radius: var(--fixed-border-radius-fix-03);
      border-top-right-radius: var(--fixed-border-radius-fix-03);
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      width: 100%;
      overflow: hidden;
      position: absolute;
      bottom: 0;
    }
  }
</style>

<style scoped>
  @media (--v-medium) {
    .dvp-dialog:has(.step--size-mini) .dvp-modal {
      width: 320px;
      margin: auto;
      position: initial;
      bottom: initial;

      border-radius: var(--fixed-border-radius-fix-03);
    }

    .dvp-dialog:has(.step--size-midi) .dvp-modal {
      width: 576px;
      margin: auto;
      position: initial;
      bottom: initial;

      border-radius: var(--fixed-border-radius-fix-03);
    }
  }
</style>

<style scoped>
  @media (--v-large) {
    .dvp-dialog:has(.step--size-midi) .dvp-modal {
      width: 700px;
    }
  }
</style>

<style scoped>
  @media (--v-wide) {
    .dvp-dialog:has(.step--size-mini) .dvp-modal {
      width: 376px;
    }
  }
</style>